import { useMemo, useState } from "react";
import { entitlementLabel } from "@/lib/auditSheetLabels";
import {
  OPERATIONAL_ROSTER_TABS,
  computeOperationalTabCounts,
  isManualRosterArchived,
  learnerMatchesOperationalTab,
  operationalLifecycleTab,
} from "@/lib/instructorRosterOperationalTabs";
import { sortLearnersByDisplayName } from "@/lib/learnerRosterSort";

const LEARNER_TYPE_OPTIONS = [
  { value: "all", label: "All learners" },
  { value: "private", label: "Private" },
  { value: "programme", label: "Programme" },
  { value: "qa", label: "QA accounts" },
];

function typeBadgeLabel(learnerType) {
  switch (learnerType) {
    case "private":
      return "Private";
    case "programme":
      return "Programme";
    case "qa":
      return "QA";
    case "unclassified":
      return "Unclassified";
    default:
      return learnerType || "—";
  }
}

function emptyMessageForType(typeFilter, listTab) {
  const tab = (listTab || "active").toLowerCase();
  if (tab === "review") {
    return "No learners need lifecycle review right now.";
  }
  if (tab === "all") {
    return "No learners match your search.";
  }
  const tabLabel = OPERATIONAL_ROSTER_TABS.find((t) => t.value === tab)?.label || tab;
  if (typeFilter !== "all") {
    return `No ${typeFilter} learners in ${tabLabel} right now.`;
  }
  return `No learners in ${tabLabel} right now.`;
}

export default function InstructorLearnerPicker({
  open,
  learners = [],
  selectedId,
  onSelect,
  qaDetailsRef,
  totalAssigned = null,
  rosterListCounts = null,
  operationalLifecycleTabCounts = null,
  lifecycleViewCounts = null,
  operationalMirrorScheduler = null,
  loading = false,
  loadError = "",
  onRetry,
  onArchiveLearner,
  onRestoreLearner,
  rosterActionBusy = false,
}) {
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [listTab, setListTab] = useState("active");

  const serverCounts = operationalLifecycleTabCounts || lifecycleViewCounts;
  const counts = useMemo(
    () => computeOperationalTabCounts(learners, serverCounts),
    [learners, serverCounts]
  );

  const sortedLearners = useMemo(() => sortLearnersByDisplayName(learners), [learners]);

  const filteredByListStatus = useMemo(() => {
    return sortedLearners.filter((l) => learnerMatchesOperationalTab(l, listTab));
  }, [sortedLearners, listTab]);

  const filteredByType = useMemo(() => {
    if (typeFilter === "all") return filteredByListStatus;
    return filteredByListStatus.filter((l) => (l.learner_type || "unclassified") === typeFilter);
  }, [filteredByListStatus, typeFilter]);

  const filteredLearners = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filteredByType;
    return filteredByType.filter(
      (l) =>
        (l.display_name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q)
    );
  }, [filteredByType, query]);

  const selectedHiddenByFilter =
    selectedId &&
    !filteredLearners.some((l) => l.id === selectedId) &&
    learners.some((l) => l.id === selectedId);

  if (!open) return null;

  if (loading || totalAssigned === null) {
    return (
      <div className="audit-surface audit-learner-picker" data-testid="audit-learner-picker">
        <h2 className="audit-section-title" style={{ marginBottom: "0.75rem" }}>
          My learners
        </h2>
        <p className="text-sm" data-testid="instructor-learner-picker-loading">
          Loading assigned learners…
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="audit-surface audit-learner-picker" data-testid="audit-learner-picker">
        <h2 className="audit-section-title" style={{ marginBottom: "0.75rem" }}>
          My learners
        </h2>
        <p className="text-sm" role="alert" data-testid="instructor-learner-picker-error">
          {loadError}
        </p>
        {onRetry && (
          <button
            type="button"
            className="audit-btn-outline-dark mt-3"
            onClick={onRetry}
            data-testid="instructor-learner-picker-retry"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  const assignedCount = totalAssigned ?? learners.length;

  return (
    <div className="audit-surface audit-learner-picker" data-testid="audit-learner-picker">
      <h2 className="audit-section-title" style={{ marginBottom: "0.75rem" }}>
        My learners
        {assignedCount > 0 && (
          <span className="text-xs font-normal ml-2" style={{ color: "rgba(22,25,28,.55)" }}>
            ({filteredLearners.length} shown)
          </span>
        )}
        {operationalMirrorScheduler?.enabled && (
          <span className="text-xs font-normal block mt-1" style={{ color: "rgba(22,25,28,.45)" }}>
            {operationalMirrorScheduler.instructor?.last_successful_feed_fetch_at
              ? `Operational feed last OK: ${operationalMirrorScheduler.instructor.last_successful_feed_fetch_at}`
              : operationalMirrorScheduler.heartbeat?.last_run_finished_at
                ? `Scheduler last run: ${operationalMirrorScheduler.heartbeat.last_run_finished_at}`
                : "Operational lifecycle mirror enabled"}
          </span>
        )}
        {operationalMirrorScheduler?.refresh_health?.overdue && (
          <span
            className="text-xs font-normal block mt-1"
            style={{ color: "rgba(180, 83, 9, .95)" }}
            data-testid="operational-mirror-overdue"
          >
            Operational source refresh is overdue — showing last known lifecycle state.
          </span>
        )}
        {operationalMirrorScheduler?.refresh_health?.feed_failed && (
          <span
            className="text-xs font-normal block mt-1"
            style={{ color: "rgba(185, 28, 28, .95)" }}
            data-testid="operational-mirror-feed-failed"
          >
            Last Operational feed fetch failed
            {operationalMirrorScheduler.instructor?.last_failure_reason_sanitized
              ? `: ${operationalMirrorScheduler.instructor.last_failure_reason_sanitized}`
              : ""}
            . Last known state preserved.
          </span>
        )}
      </h2>
      {assignedCount === 0 ? (
        <p className="text-sm" data-testid="instructor-no-assignments">
          No learners are assigned to your Academy instructor account yet. Ask an administrator to assign training
          access, or open training from GDI using Start Quick Feedback on an assigned student.
          {operationalMirrorScheduler?.enabled && operationalMirrorScheduler?.has_gdi_instructor_link && (
            <span className="block mt-2 text-xs" style={{ color: "rgba(22,25,28,.55)" }}>
              Your instructor account is linked to Operational GDI; lifecycle mirror runs apply when learners are
              assigned.
            </span>
          )}
        </p>
      ) : (
        <>
          <div
            className="flex flex-wrap gap-2 mb-3"
            role="tablist"
            aria-label="Operational lifecycle status"
            data-testid="instructor-roster-list-tabs"
          >
            {OPERATIONAL_ROSTER_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={listTab === tab.value}
                className={`px-3 py-1.5 text-sm rounded border min-h-11 ${
                  listTab === tab.value ? "font-semibold" : ""
                }`}
                style={{ borderColor: "rgba(22,25,28,.2)" }}
                onClick={() => setListTab(tab.value)}
                data-testid={`instructor-roster-tab-${tab.value}`}
              >
                {tab.label} ({counts[tab.value] ?? 0})
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-end">
            <label className="block space-y-1 flex-1 min-w-0" data-testid="instructor-learner-type-wrap">
              <span className="text-xs font-medium" id="instructor-learner-type-label">
                Learner type
              </span>
              <select
                className="w-full px-3 py-2 border rounded text-sm min-h-11"
                style={{ borderColor: "rgba(22,25,28,.2)" }}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-labelledby="instructor-learner-type-label"
                data-testid="instructor-learner-type-filter"
              >
                {LEARNER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            {assignedCount > 3 && (
              <label className="block space-y-1 flex-1 min-w-0" data-testid="instructor-learner-search-wrap">
                <span className="text-xs font-medium">Search learners</span>
                <input
                  type="search"
                  className="w-full px-3 py-2 border rounded text-sm min-h-11"
                  style={{ borderColor: "rgba(22,25,28,.2)" }}
                  placeholder="Name or email"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search assigned learners by name or email"
                  data-testid="instructor-learner-search"
                />
              </label>
            )}
          </div>

          {selectedHiddenByFilter && (
            <p className="text-sm mb-3" role="status" data-testid="instructor-learner-filter-notice">
              Your selected learner is hidden by the current filter. Choose another learner or adjust list or type
              filters.
            </p>
          )}

          {filteredLearners.length === 0 ? (
            <p className="text-sm" data-testid="instructor-learner-filter-empty">
              {query.trim() ? emptyMessageForType("all", listTab) : emptyMessageForType(typeFilter, listTab)}
            </p>
          ) : (
            <ul
              className="audit-learner-picker-grid"
              style={{ listStyle: "none", margin: 0, padding: 0 }}
              data-testid="instructor-learner-list"
              ref={qaDetailsRef}
            >
              {filteredLearners.map((l) => {
                const isQa = (l.learner_type || "") === "qa";
                const otab = operationalLifecycleTab(l);
                const manualArchived = isManualRosterArchived(l);
                const needsReview = otab === "review";
                const lifecycleBadge =
                  l.lifecycle_badge && l.lifecycle_badge !== "—" ? l.lifecycle_badge : null;
                const badge = lifecycleBadge || typeBadgeLabel(l.learner_type);
                const opLabel = l.operational_status_label;
                return (
                  <li key={l.id}>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        className={`audit-learner-pick-btn w-full ${selectedId === l.id ? "is-selected" : ""}`}
                        style={isQa ? { borderStyle: "dashed" } : undefined}
                        onClick={(e) => {
                          e.preventDefault();
                          onSelect(l);
                        }}
                        aria-label={`Open training for ${l.display_name}`}
                        aria-current={selectedId === l.id ? "true" : undefined}
                        data-testid={
                          isQa ? `instructor-learner-qa-${l.id}` : `instructor-learner-${l.id}`
                        }
                      >
                        <span className="font-semibold text-sm">{l.display_name}</span>
                        <span className="flex flex-wrap gap-1 mt-1">
                          <span
                            className="text-xs px-1.5 py-0.5 rounded"
                            style={{
                              background: "rgba(22,25,28,.06)",
                              color: "rgba(22,25,28,.75)",
                            }}
                            data-testid={`instructor-learner-type-badge-${l.id}`}
                          >
                            {badge}
                          </span>
                          {opLabel && opLabel !== "—" && !manualArchived && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{ background: "rgba(22,25,28,.04)", color: "rgba(22,25,28,.65)" }}
                              data-testid={`instructor-learner-op-status-${l.id}`}
                            >
                              {opLabel}
                            </span>
                          )}
                          {needsReview && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{ background: "rgba(185, 28, 28, .12)", color: "rgba(153, 27, 27, .95)" }}
                              data-testid={`instructor-learner-review-badge-${l.id}`}
                            >
                              Review lifecycle
                            </span>
                          )}
                          {manualArchived && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{ background: "rgba(22,25,28,.1)", color: "rgba(22,25,28,.8)" }}
                              data-testid={`instructor-learner-manual-archive-badge-${l.id}`}
                            >
                              Archived (my list)
                            </span>
                          )}
                          {l.operational_mirror_stale && (
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: "rgba(200,120,0,.15)" }}>
                              Stale source
                            </span>
                          )}
                        </span>
                        <span className="text-xs mt-1 block" style={{ color: "rgba(22,25,28,.6)" }}>
                          {entitlementLabel(l.training_entitlement)}
                        </span>
                      </button>
                      {manualArchived && onRestoreLearner && (
                        <button
                          type="button"
                          className="audit-btn-outline-dark text-xs py-1"
                          disabled={rosterActionBusy}
                          onClick={() => onRestoreLearner(l)}
                          data-testid={`instructor-learner-restore-${l.id}`}
                        >
                          Restore to active list
                        </button>
                      )}
                      {!manualArchived && listTab === "active" && onArchiveLearner && (
                        <button
                          type="button"
                          className="text-xs py-1 underline"
                          style={{ color: "rgba(22,25,28,.55)" }}
                          disabled={rosterActionBusy}
                          onClick={() => onArchiveLearner(l)}
                          data-testid={`instructor-learner-archive-${l.id}`}
                        >
                          Archive from my list
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
